/**
 * Type for cron field values. Can be:
 * - A specific number
 * - A wildcard string "*"
 * - A range string "1-5"
 * - A list string "1,3,5"
 * - A step string "* /5" or "1-10/2"
 */
export type CronField = number | string

/**
 * Options for generating a cron string
 */
export interface CronOptions {
  /** Minute field (0-59). Default: "*" */
  minute?: CronField
  /** Hour field (0-23). Default: "*" */
  hour?: CronField
  /** Day of month field (1-31). Default: "*" */
  dayOfMonth?: CronField
  /** Month field (1-12). Default: "*" */
  month?: CronField
  /** Day of week field (0-6, where 0 is Sunday). Default: "*" */
  dayOfWeek?: CronField
  /** Optional second field (0-59) for extended cron format. Default: undefined */
  second?: CronField
}

/**
 * Generates a cron string based on the provided options.
 *
 * Supports both standard 5-field cron format and extended 6-field format (with seconds).
 *
 * @param options - Configuration for each cron field
 * @returns A valid cron string
 *
 * @example
 * // Every minute
 * generateCronString(); // "* * * * *"
 *
 * @example
 * // Every day at 2:30 AM
 * generateCronString({ minute: 30, hour: 2 }); // "30 2 * * *"
 *
 * @example
 * // Every 15 minutes
 * generateCronString({ minute: "* /15" }); // "* /15 * * * *"
 *
 * @example
 * // Every weekday at 9 AM
 * generateCronString({ minute: 0, hour: 9, dayOfWeek: "1-5" }); // "0 9 * * 1-5"
 *
 * @example
 * // First day of every month at midnight
 * generateCronString({ minute: 0, hour: 0, dayOfMonth: 1 }); // "0 0 1 * *"
 *
 * @example
 * // Every 30 seconds (extended format with seconds)
 * generateCronString({ second: "* /30" }); // "* /30 * * * * *"
 *
 * @example
 * // Multiple specific values
 * generateCronString({ minute: "0,15,30,45", hour: "9,12,15" }); // "0,15,30,45 9,12,15 * * *"
 *
 * @example
 * // Complex pattern: Every 5 minutes during business hours on weekdays
 * generateCronString({
 *   minute: "* /5",
 *   hour: "9-17",
 *   dayOfWeek: "1-5"
 * }); // "* /5 9-17 * * 1-5"
 */
export function generateCronString(options: CronOptions = {}): string {
  const { minute = '*', hour = '*', dayOfMonth = '*', month = '*', dayOfWeek = '*', second } = options

  // Normalize field values to strings
  const normalizeField = (field: CronField): string => {
    return typeof field === 'number' ? field.toString() : field
  }

  const fields = [
    normalizeField(minute),
    normalizeField(hour),
    normalizeField(dayOfMonth),
    normalizeField(month),
    normalizeField(dayOfWeek),
  ]

  // If second is specified, prepend it for extended cron format
  if (second !== undefined) {
    fields.unshift(normalizeField(second))
  }

  return fields.join(' ')
}

/**
 * Pre-defined common cron patterns for convenience
 */
export const CronPatterns = {
  /** Every minute */
  EVERY_MINUTE: generateCronString(),

  /** Every hour at minute 0 */
  EVERY_HOUR: generateCronString({ minute: 0 }),

  /** Every day at midnight */
  DAILY: generateCronString({ minute: 0, hour: 0 }),

  /** Every Sunday at midnight */
  WEEKLY: generateCronString({ minute: 0, hour: 0, dayOfWeek: 0 }),

  /** First day of every month at midnight */
  MONTHLY: generateCronString({ minute: 0, hour: 0, dayOfMonth: 1 }),

  /** First day of January at midnight */
  YEARLY: generateCronString({ minute: 0, hour: 0, dayOfMonth: 1, month: 1 }),

  /** Every 5 minutes */
  EVERY_5_MINUTES: generateCronString({ minute: '*/5' }),

  /** Every 15 minutes */
  EVERY_15_MINUTES: generateCronString({ minute: '*/15' }),

  /** Every 30 minutes */
  EVERY_30_MINUTES: generateCronString({ minute: '*/30' }),

  /** Every weekday (Monday-Friday) at 9 AM */
  WEEKDAYS_9AM: generateCronString({ minute: 0, hour: 9, dayOfWeek: '1-5' }),

  /** Every 30 seconds (extended format) */
  EVERY_30_SECONDS: generateCronString({ second: '*/30' }),
} as const

/**
 * Helper functions for common cron patterns
 */

/**
 * Generate a cron string for a specific time each day
 * @param hour - Hour (0-23)
 * @param minute - Minute (0-59), default 0
 */
export function dailyAt(hour: number, minute = 0): string {
  return generateCronString({ minute, hour })
}

/**
 * Generate a cron string for a specific time on specific days of the week
 * @param dayOfWeek - Day(s) of week (0-6, or range/list like "1-5")
 * @param hour - Hour (0-23)
 * @param minute - Minute (0-59), default 0
 */
export function weeklyOn(dayOfWeek: CronField, hour: number, minute = 0): string {
  return generateCronString({ minute, hour, dayOfWeek })
}

/**
 * Generate a cron string for a specific day of the month
 * @param dayOfMonth - Day of month (1-31)
 * @param hour - Hour (0-23), default 0
 * @param minute - Minute (0-59), default 0
 */
export function monthlyOn(dayOfMonth: number, hour = 0, minute = 0): string {
  return generateCronString({ minute, hour, dayOfMonth })
}

/**
 * Generate a cron string for every N minutes
 * @param interval - Number of minutes between executions
 */
export function everyMinutes(interval: number): string {
  return generateCronString({ minute: `*/${interval}` })
}

/**
 * Generate a cron string for every N hours
 * @param interval - Number of hours between executions
 */
export function everyHours(interval: number): string {
  return generateCronString({ minute: 0, hour: `*/${interval}` })
}
